import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded as AddIcon,
  Close as CloseIcon,
  EventBusyRounded as EventBusyIcon,
  ScheduleRounded as ScheduleIcon,
} from '@mui/icons-material';
import { api, ConnectorAppointment, ConnectorScheduleDay } from '../../services/api';

interface ManageConnectorScheduleDialogProps {
  open: boolean;
  onClose: () => void;
}

const ORANGE = '#ed8936';
const SGT_TIME_ZONE = 'Asia/Singapore';
const WEEKDAYS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
];

function emptyDays(): ConnectorScheduleDay[] {
  return WEEKDAYS.map((w) => ({
    dayOfWeek: w.value,
    enabled: false,
    startTime: '09:00',
    endTime: '18:00',
    slotMinutes: 30,
  }));
}

function todayInSingapore(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: SGT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

function formatAppointment(value: string): { date: string; time: string } {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return { date: '—', time: '—' };
  return {
    date: new Intl.DateTimeFormat('en-GB', {
      timeZone: SGT_TIME_ZONE,
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date),
    time: new Intl.DateTimeFormat('en-US', {
      timeZone: SGT_TIME_ZONE,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date),
  };
}

function formatExcludedDate(dateKey: string): string {
  const date = new Date(`${dateKey}T00:00:00+08:00`);
  if (!Number.isFinite(date.getTime())) return dateKey;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: SGT_TIME_ZONE,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

const ManageConnectorScheduleDialog: React.FC<ManageConnectorScheduleDialogProps> = ({ open, onClose }) => {
  const [tab, setTab] = useState(0);
  const [appointments, setAppointments] = useState<ConnectorAppointment[]>([]);
  const [days, setDays] = useState<ConnectorScheduleDay[]>(emptyDays());
  const [sessionMinutes, setSessionMinutes] = useState(30);
  const [excludedDates, setExcludedDates] = useState<string[]>([]);
  const [newExcludedDate, setNewExcludedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const minDate = useMemo(todayInSingapore, []);

  const loadData = useCallback(async () => {
    setError(null);
    setSaved(false);
    setLoading(true);
    try {
      const [schedule, loadedAppointments] = await Promise.all([
        api.getConnectorSchedule(),
        api.getConnectorAppointments(),
      ]);
      const byDay = new Map(schedule.days.map((day) => [day.dayOfWeek, day]));
      const merged = WEEKDAYS.map((weekday) => byDay.get(weekday.value) ?? emptyDays()[weekday.value - 1]);
      setDays(merged);
      setSessionMinutes(merged.find((day) => day.enabled)?.slotMinutes ?? merged[0]?.slotMinutes ?? 30);
      setExcludedDates(schedule.excludedDates);
      setAppointments(loadedAppointments);
    } catch (err) {
      console.error('Failed to load connector appointments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load connector appointments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setNewExcludedDate('');
    void loadData();
  }, [open, loadData]);

  const updateDay = useCallback((dayOfWeek: number, patch: Partial<ConnectorScheduleDay>) => {
    setDays((previous) => previous.map((day) => (day.dayOfWeek === dayOfWeek ? { ...day, ...patch } : day)));
    setSaved(false);
  }, []);

  const addExcludedDate = useCallback(() => {
    if (!newExcludedDate) return;
    setExcludedDates((previous) => Array.from(new Set([...previous, newExcludedDate])).sort());
    setNewExcludedDate('');
    setSaved(false);
  }, [newExcludedDate]);

  const handleSave = useCallback(async () => {
    setError(null);
    for (const day of days) {
      if (day.enabled && day.endTime <= day.startTime) {
        const label = WEEKDAYS.find((weekday) => weekday.value === day.dayOfWeek)?.label ?? `Day ${day.dayOfWeek}`;
        setError(`${label}: end time must be after start time.`);
        return;
      }
    }
    if (!Number.isFinite(sessionMinutes) || sessionMinutes < 5 || sessionMinutes > 480) {
      setError('Session length must be between 5 and 480 minutes.');
      return;
    }
    setSaving(true);
    try {
      const payload = days.map((day) => ({ ...day, slotMinutes: sessionMinutes }));
      const result = await api.saveConnectorSchedule(payload, excludedDates);
      const byDay = new Map(result.days.map((day) => [day.dayOfWeek, day]));
      setDays(WEEKDAYS.map((weekday) => byDay.get(weekday.value) ?? emptyDays()[weekday.value - 1]));
      setExcludedDates(result.excludedDates);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  }, [days, excludedDates, sessionMinutes]);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)', backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ pb: 0, borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon sx={{ color: ORANGE }} /> Connector Appointments
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              View booked appointments and manage office availability.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Tabs
          value={tab}
          onChange={(_, value) => { setTab(value); setError(null); setSaved(false); }}
          sx={{ mt: 1.5, minHeight: 42, '& .MuiTabs-indicator': { bgcolor: ORANGE } }}
        >
          <Tab label={`Appointments (${appointments.length})`} sx={{ minHeight: 42 }} />
          <Tab label="Manage schedule" sx={{ minHeight: 42 }} />
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, minHeight: 390 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : tab === 0 ? (
          appointments.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <ScheduleIcon sx={{ fontSize: 42, color: 'rgba(var(--color-text-rgb), 0.25)', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">No appointments have been booked.</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: '55vh', border: '1px solid rgba(var(--color-text-rgb), 0.1)', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Appointment</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Invite</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map((appointment) => {
                    const when = formatAppointment(appointment.appointmentAt);
                    return (
                      <TableRow key={appointment.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{when.date}</Typography>
                          <Typography variant="caption" color="text.secondary">{when.time} SGT</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{appointment.agentName || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{appointment.cea}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{appointment.mobile || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{appointment.email || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{appointment.durationMinutes} min</TableCell>
                        <TableCell sx={{ textTransform: 'uppercase' }}>{appointment.calendarMethod || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          <>
            {WEEKDAYS.map((weekday) => {
              const day = days.find((candidate) => candidate.dayOfWeek === weekday.value)!;
              return (
                <Box
                  key={weekday.value}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '150px 1fr 1fr' },
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    borderBottom: '1px solid rgba(var(--color-text-rgb), 0.06)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Switch
                      checked={day.enabled}
                      onChange={(event) => updateDay(weekday.value, { enabled: event.target.checked })}
                      sx={{ '& .Mui-checked': { color: ORANGE }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: ORANGE } }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{weekday.label}</Typography>
                  </Box>
                  <TextField
                    label="From"
                    type="time"
                    size="small"
                    value={day.startTime}
                    disabled={!day.enabled}
                    onChange={(event) => updateDay(weekday.value, { startTime: event.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                  <TextField
                    label="To"
                    type="time"
                    size="small"
                    value={day.endTime}
                    disabled={!day.enabled}
                    onChange={(event) => updateDay(weekday.value, { endTime: event.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </Box>
              );
            })}

            <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TextField
                label="Session length"
                type="number"
                size="small"
                value={sessionMinutes}
                onChange={(event) => { setSessionMinutes(Number(event.target.value)); setSaved(false); }}
                inputProps={{ min: 5, max: 480, step: 5 }}
                sx={{ width: 160 }}
              />
              <Typography variant="body2" color="text.secondary">minutes per appointment</Typography>
            </Box>

            <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <EventBusyIcon sx={{ color: ORANGE, fontSize: 20 }} /> Excluded dates
              </Typography>
              <Typography variant="caption" color="text.secondary">
                No slots will be offered on these dates, even when their weekday is enabled.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1.5 }}>
                <TextField
                  label="Date to exclude"
                  type="date"
                  size="small"
                  value={newExcludedDate}
                  onChange={(event) => setNewExcludedDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDate }}
                />
                <Button variant="outlined" startIcon={<AddIcon />} disabled={!newExcludedDate} onClick={addExcludedDate}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: excludedDates.length ? 1.5 : 0 }}>
                {excludedDates.map((date) => (
                  <Chip
                    key={date}
                    label={formatExcludedDate(date)}
                    onDelete={() => { setExcludedDates((previous) => previous.filter((item) => item !== date)); setSaved(false); }}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>

            {saved && !error && <Alert severity="success" sx={{ mt: 2 }}>Schedule saved.</Alert>}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)', px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>Close</Button>
        {tab === 1 && (
          <Button
            onClick={handleSave}
            disabled={loading || saving}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ bgcolor: ORANGE, color: '#1a1206', fontWeight: 700, '&:hover': { bgcolor: '#d97b28' } }}
          >
            Save Schedule
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ManageConnectorScheduleDialog;
